import alt from 'components/Dispatcher';
import generateAsyncActions from 'utils/generateAsyncActions';
import CoachingReviewSource from 'sources/coaching/CoachingReviewSource';

class CoachingReviewActions {
  constructor() {
    this.generateActions(
      'setReviewRedirect',
      'setCurrentlyFillingOutReview',
      'clearCurrentlyFillingOutReview',
      'setViewingScorecardForReview',
      'prepReview',
      ...generateAsyncActions(
        'fetchReviewFeedback',
        'updateReviewFileUploads',
        'deleteReviewFileUpload',
        'saveForLater',
        'changeFileReviewSkills',
        'submitFeedback',
        'updateReview',
        'rescheduleReview',
        'deleteReview',
        'startReview',
        'cancelReview'
      )
    );
  }

  fetchReviewFeedback(reviewId) {
    return dispatch => {
      dispatch(reviewId);
      return CoachingReviewSource.fetchReviewFeedback(reviewId).then(
        results => {
          this.fetchReviewFeedbackSuccess.defer(reviewId, results);
          return results;
        },
        error => {
          this.fetchReviewFeedbackError.defer(reviewId);
          throw error;
        }
      );
    };
  }

  updateReviewFileUploads(reviewId, reviewFileUploads) {
    return dispatch => {
      dispatch([reviewId, reviewFileUploads]);
      return CoachingReviewSource.updateReviewFileUploads(reviewId, reviewFileUploads).then(
        this.updateReviewFileUploadsSuccess,
        this.updateReviewFileUploadsError
      );
    };
  }

  deleteReviewFileUpload(reviewId, fileUploadId) {
    return dispatch => {
      dispatch(reviewId);
      return CoachingReviewSource.deleteReviewFileUpload(reviewId, fileUploadId).then(
        this.deleteReviewFileUploadSuccess,
        this.deleteReviewFileUploadError
      );
    };
  }

  saveForLater(review, reviewFileUpload) {
    return dispatch => {
      dispatch([review, reviewFileUpload]);
      return CoachingReviewSource.saveForLater(review, reviewFileUpload).then(
        this.saveForLaterSuccess,
        this.saveForLaterError
      );
    };
  }

  changeFileReviewSkills(reviewId, reviewFileUploadId, contentDetailIds) {
    return dispatch => {
      dispatch([reviewId, reviewFileUploadId, contentDetailIds]);
      return CoachingReviewSource.changeFileReviewSkills(
        reviewId,
        reviewFileUploadId,
        contentDetailIds
      ).then(this.changeFileReviewSkillsSuccess, this.changeFileReviewSkillsError);
    };
  }

  submitFeedback(review, reviewFileUploads, reviewTypeAnswers) {
    return dispatch => {
      dispatch(review);
      return CoachingReviewSource.submitFeedback(review, reviewFileUploads, reviewTypeAnswers).then(
        feedback => this.submitFeedbackSuccess([review, feedback]),
        this.submitFeedbackError
      );
    };
  }

  updateReview(review) {
    return dispatch => {
      dispatch(review);
      return CoachingReviewSource.updateReview(review).then(
        () => this.updateReviewSuccess(review),
        this.updateReviewError
      );
    };
  }

  rescheduleReview(review) {
    return dispatch => {
      dispatch(review);
      return CoachingReviewSource.rescheduleReview(review).then(
        () => this.updateReviewSuccess(review),
        this.updateReviewError
      );
    };
  }

  deleteReview(reviewId) {
    return dispatch => {
      dispatch(reviewId);
      return CoachingReviewSource.deleteReview(reviewId).then(
        this.deleteReviewSuccess,
        this.deleteReviewError
      );
    };
  }

  /**
   * Begin filling out a review.
   *
   * Sets the review id that is currently being filled in the store (for other components to listen to),
   * as well as starts the coaching timer if the review was started by the coach & not the recipient.
   *
   * @param reviewId
   * @param isCoach
   */
  startReview(reviewId, isCoach) {
    return dispatch => {
      dispatch(reviewId);

      return isCoach
        ? CoachingReviewSource.startReview(reviewId)
        : Promise.resolve().then(this.startReviewSuccess, this.startReviewError);
    };
  }

  cancelReview(reviewId) {
    return dispatch => {
      dispatch(reviewId);

      return CoachingReviewSource.cancelReview(reviewId).then(
        this.cancelReviewSuccess,
        this.cancelReviewError
      );
    };
  }

  setLiveCoachingStubs(reviewId, stubs) {
    if (stubs < 0) {
      return;
    }

    return dispatch => {
      dispatch(reviewId);
      return CoachingReviewSource.setLiveCoachingStubs(reviewId, stubs).then(
        data => this.updateReviewSuccess(data),
        this.updateReviewError
      );
    };
  }
}

export default alt.createActions(CoachingReviewActions);
